import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Trophy,
  Crown,
  Shield,
  Star,
  StarOff,
  Sparkles,
  Swords,
  Lock,
  Coins,
  Award,
  Zap,
  ArrowDownWideNarrow,
  ArrowUpNarrowWide,
  ExternalLink,
  History,
  Copy,
  Check,
  Flame,
} from "lucide-react";
import { toast } from "sonner";
import { playerApi } from "@/lib/api";
import NavMenu from "@/components/NavMenu";
import AuthWidget from "@/components/AuthWidget";
import { useAuth } from "@/context/AuthContext";
import {
  getGuestHistory,
  addGuestHistory,
  removeGuestHistory,
} from "@/lib/playerHistory";
import { Button } from "@/components/ui/button";
import ClanBadge from "@/components/ClanBadge";
import CollectionRewardsPanel from "@/components/CollectionRewardsPanel";
import { WILDCARD_IMG, WILDCARD_TR } from "@/lib/collectionRewards";
import {
  RARITY_RANK,
  RARITY_TR,
  RARITY_COLOR,
  CRYSTAL_RATE,
  progressBarInfo,
  toDisplayLevel,
  computeMaxSummary,
  computeHeroSummary,
  computeCollectionLevelPlan,
  mergeConsecutiveUpgrades,
  computeMaxPossibleCL,
} from "@/lib/maxCalc";

const ROLE_TR = {
  leader: "Lider",
  coLeader: "Yardımcı Lider",
  elder: "Kıdemli",
  member: "Üye",
};

const CRYSTAL_ICON =
  "https://customer-assets.emergentagent.com/job_09914adb-7788-4174-b308-530e1c00f18f/artifacts/xpshc3t6_zv86QhiHc9G4GQrMdETi.png";
const GOLD_ICON = "/rewards/gold.png";

const fmt = (n) => (n == null ? "-" : Number(n).toLocaleString("tr-TR"));

function Stat({ icon: Icon, label, value, tone = "text-foreground", sub, iconImg }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        {iconImg ? (
          <img src={iconImg} alt="" className="h-4 w-4 object-contain" />
        ) : Icon ? (
          <Icon className="h-3.5 w-3.5" />
        ) : null}
        {label}
      </div>
      <div className={`mt-0.5 font-bold tabular-nums text-base sm:text-lg ${tone}`}>
        {value}
      </div>
      {sub != null && (
        <div className="text-[10px] text-muted-foreground tabular-nums mt-0.5">{sub}</div>
      )}
    </div>
  );
}

function CardImg({ item, className = "" }) {
  const [src, setSrc] = useState(item.icon || item.icon_alt || "");
  const [failed, setFailed] = useState(false);
  const onErr = () => {
    if (src !== item.icon_alt && item.icon_alt) {
      setSrc(item.icon_alt);
    } else {
      setFailed(true);
    }
  };
  if (!src || failed) {
    return <div className={`bg-white/5 rounded ${className}`} />;
  }
  return (
    <img
      src={src}
      alt={item.name}
      loading="lazy"
      onError={onErr}
      className={`object-contain drop-shadow ${className}`}
    />
  );
}

function ProgressBar({ current, max, tone = "primary", height = "h-1.5" }) {
  const pct = max > 0 ? Math.min(100, (current / max) * 100) : 0;
  const over = current > max;
  const barColor =
    tone === "amber"
      ? "bg-amber-400"
      : tone === "fuchsia"
      ? "bg-fuchsia-400"
      : tone === "rose"
      ? "bg-rose-400"
      : "bg-primary";
  return (
    <div className={`w-full ${height} rounded-full bg-white/10 overflow-hidden relative`}>
      <div
        className={`${barColor} h-full transition-all`}
        style={{ width: `${pct}%` }}
      />
      {over && (
        <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-white/80">
          ✓
        </span>
      )}
    </div>
  );
}

function CardTile({ item, locked = false, evo = false, hideCount = false, rawLevel = false }) {
  // NOT: Kule kartları (support cards) da CR API'sinden normal kartlar gibi
  // nadirliğe göre "ham" seviyeyle gelir; bu yüzden görüntü seviyesi HER ZAMAN
  // toDisplayLevel ile hesaplanır. MAX durumu ise kartın kendi max_level'ına göre.
  const displayLevel = !locked
    ? toDisplayLevel(item.level, item.rarity)
    : null;
  const info = !locked ? progressBarInfo(item) : null;
  const isMax = rawLevel
    ? (item.level != null && item.max_level != null && item.level >= item.max_level)
    : info?.isMax;
  const count = item.count || 0;
  const need = info?.neededNext;
  return (
    <div
      className={`relative rounded-lg border p-1 flex flex-col ${
        locked
          ? "border-white/5 bg-black/30 opacity-45 grayscale"
          : "border-white/10 bg-white/[0.03]"
      }`}
      title={item.name}
    >
      <div className="relative w-full aspect-square flex items-center justify-center overflow-hidden">
        <CardImg item={item} className="w-full h-full" />
        {locked && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Lock className="h-4 w-4 text-white/70" />
          </span>
        )}
        {!locked && !hideCount && count > 0 && (
          <span className="absolute -top-1 -right-1 rounded-md bg-sky-500/90 text-white text-[9px] font-bold px-1 py-0.5 leading-none shadow">
            x{count > 999 ? `${Math.floor(count / 1000)}k` : count}
          </span>
        )}
        {evo && !locked && (
          <span className="absolute -top-1 -left-1 rounded-md bg-fuchsia-500/90 text-white text-[8px] font-bold px-1 py-0.5 leading-none shadow">
            EVO
          </span>
        )}
      </div>
      <div className="mt-0.5 w-full text-center">
        <div className="text-[8px] leading-tight truncate text-muted-foreground">
          {item.name}
        </div>
        {!locked && displayLevel != null && (
          <div
            className={`text-[9px] font-bold tabular-nums ${
              isMax ? "text-amber-300" : "text-foreground"
            }`}
          >
            {isMax ? "MAX" : `Sv ${displayLevel}`}
          </div>
        )}
        {!locked && !isMax && need && !hideCount && (
          <>
            <div className="text-[8px] leading-none tabular-nums text-muted-foreground">
              {fmt(count)}/{fmt(need)}
            </div>
            <div className="mt-0.5">
              <ProgressBar current={count} max={need} height="h-1" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, subtitle, children, id }) {
  return (
    <section className="card-surface rounded-2xl border border-white/10 p-3 sm:p-5" id={id}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && (
          <span className="rounded-md p-1.5 bg-primary/15 text-primary">
            <Icon className="h-4 w-4" strokeWidth={2.5} />
          </span>
        )}
        <h2 className="font-display text-base sm:text-lg tracking-wide">
          {title}
        </h2>
        {subtitle && (
          <span className="ml-auto text-[11px] sm:text-xs font-semibold text-muted-foreground tabular-nums">
            {subtitle}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

/** Aşama/Lig etiketi.
 * Kullanıcı Efsaneler Yolu'ndaysa "Lig X" olarak gösterilir.
 * Aksi halde arena adı.
 */
function leagueLabel(id) {
  const inPoL = id.pol_league != null || (id.pol_trophies || 0) > 0;
  if (inPoL) {
    return {
      main: `Lig ${id.pol_league ?? "?"}`,
      tone: "text-amber-300",
      sub: null,
    };
  }
  return { main: id.arena || "-", tone: "text-primary", sub: null };
}

export default function PlayerProfile() {
  const { tag } = useParams();
  const { isAuthed } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await playerApi.get(tag);
      setData(d);
      if (!isAuthed) {
        // Misafir: profili tarayıcı geçmişine kaydet, kayıt durumunu geçmişe göre belirle
        const hist = addGuestHistory(tag, d?.identity?.name || "");
        setSaved(hist.some((s) => s.tag === tag.replace(/^#/, "").toUpperCase()));
      } else {
        try {
          const list = await playerApi.listSaved();
          setSaved(list.some((s) => s.tag.replace("#", "") === tag.replace("#", "")));
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? "Oyuncu bulunamadı. Tag'ı kontrol edin."
          : err?.response?.data?.detail || "Oyuncu bilgisi alınamadı.",
      );
    } finally {
      setLoading(false);
    }
  }, [tag, isAuthed]);

  useEffect(() => {
    load();
  }, [load]);

  // Aktiflik durumu — her 60 saniyede hafif endpoint'i yoklar
  // + sekme tekrar fokus/görünür olunca anında bir kere daha kontrol eder
  useEffect(() => {
    if (!data?.identity?.tag) return undefined;
    const check = async () => {
      try {
        const act = await playerApi.activity(tag);
        setData((prev) =>
          prev
            ? { ...prev, identity: { ...prev.identity, activity: act } }
            : prev,
        );
      } catch {
        /* ignore */
      }
    };
    const iv = setInterval(check, 60_000);
    const onVis = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", check);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [data?.identity?.tag, tag]);

  const toggleSave = async () => {
    if (!data) return;
    if (!isAuthed) {
      // Misafir: tarayıcı geçmişinde tut/çıkar
      if (saved) {
        removeGuestHistory(data.identity.tag);
        setSaved(false);
        toast.success("Geçmişten çıkarıldı");
      } else {
        addGuestHistory(data.identity.tag, data.identity.name);
        setSaved(true);
        toast.success("Geçmişe eklendi");
      }
      return;
    }
    try {
      if (saved) {
        await playerApi.removeSaved(data.identity.tag);
        setSaved(false);
        toast.success("Kayıttan çıkarıldı");
      } else {
        await playerApi.save(data.identity.tag, data.identity.name);
        setSaved(true);
        toast.success("Kaydedildi");
      }
    } catch {
      toast.error("İşlem başarısız");
    }
  };

  // UI state
  const [activeTab, setActiveTab] = useState("max");
  const [cardSortMode, setCardSortMode] = useState("rarity"); // "rarity" | "maxness"
  const [cardSortDir, setCardSortDir] = useState("desc"); // "desc" | "asc"
  const [battles, setBattles] = useState(null);
  const [battlesLoading, setBattlesLoading] = useState(false);

  // Koleksiyon Seviyesi Hesaplama Motoru state'i (input'lar string tutuluyor -> "0x" bug'ı yok)
  const [clTarget, setClTarget] = useState("");
  const [clNewEvos, setClNewEvos] = useState("");
  const [clNewHeroes, setClNewHeroes] = useState("");
  const [clResult, setClResult] = useState(null);
  const [clMode, setClMode] = useState(null); // "joker" | "nojoker" | null
  // Joker seçim submenu: null | "auto" | "manual"
  const [clJokerVariant, setClJokerVariant] = useState(null);
  // Manuel modda kullanıcının izin verdiği joker miktarları
  const [clManualJokers, setClManualJokers] = useState({
    common: "",
    rare: "",
    epic: "",
    legendary: "",
    champion: "",
  });

  // Battle log'u ilk kez sekmeye tıklandığında lazy yükle
  useEffect(() => {
    if (activeTab !== "battles" || battles !== null) return;
    setBattlesLoading(true);
    playerApi
      .battles(tag, 25)
      .then((res) => setBattles(res.battles || []))
      .catch(() => setBattles([]))
      .finally(() => setBattlesLoading(false));
  }, [activeTab, battles, tag]);

  // Kartların "max'a yakınlık" oranı 0..1
  const cardMaxProgress = useCallback((card) => {
    const rarity = card.rarity;
    const displayLevel = toDisplayLevel(card.level, rarity);
    if (displayLevel == null) return 0;
    return Math.max(0, Math.min(1, displayLevel / 16));
  }, []);

  const cardsSorted = useMemo(() => {
    if (!data?.cards) return [];
    const dir = cardSortDir === "asc" ? 1 : -1;
    const arr = [...data.cards];
    if (cardSortMode === "maxness") {
      arr.sort((a, b) => (cardMaxProgress(a) - cardMaxProgress(b)) * dir);
    } else {
      arr.sort((a, b) => {
        const r =
          ((RARITY_RANK[a.rarity] ?? 9) - (RARITY_RANK[b.rarity] ?? 9)) *
          (cardSortDir === "asc" ? -1 : 1);
        if (r !== 0) return r;
        const la = toDisplayLevel(a.level, a.rarity) || 0;
        const lb = toDisplayLevel(b.level, b.rarity) || 0;
        return (lb - la) * (cardSortDir === "asc" ? -1 : 1);
      });
    }
    return arr;
  }, [data, cardSortMode, cardSortDir, cardMaxProgress]);

  const lockedCardsSorted = useMemo(() => {
    if (!data?.locked_cards) return [];
    const dir = cardSortDir === "asc" ? 1 : -1;
    return [...data.locked_cards].sort(
      (a, b) => ((RARITY_RANK[a.rarity] ?? 9) - (RARITY_RANK[b.rarity] ?? 9)) * dir,
    );
  }, [data, cardSortDir]);

  const evolutionsSorted = useMemo(() => {
    if (!data?.evolutions) return [];
    return [...data.evolutions].sort(
      (a, b) => (b.unlocked ? 1 : 0) - (a.unlocked ? 1 : 0),
    );
  }, [data]);

  const heroesSorted = useMemo(() => {
    if (!data?.heroes) return [];
    return [...data.heroes].sort(
      (a, b) => (b.unlocked ? 1 : 0) - (a.unlocked ? 1 : 0),
    );
  }, [data]);

  const maxSummary = useMemo(() => {
    if (!data) return null;
    // Kule Birliği (Tower Troops / support cards) da normal kartlar gibi max
    // hesabına dahil edilir — altın + joker (wild card) toplamları doğru olsun.
    const owned = [...(data.cards || []), ...(data.support_cards || [])];
    return computeMaxSummary(owned, data.locked_cards);
  }, [data]);

  // Koleksiyon Seviyesi hesaplayıcıya normal kartlar + kule kartları birlikte girer
  // (kule kartları da seviye başına +1 koleksiyon seviyesi sağlar).
  const clCards = useMemo(
    () => [...(data?.cards || []), ...(data?.support_cards || [])],
    [data],
  );

  const heroSummary = useMemo(() => {
    if (!data) return null;
    return computeHeroSummary(data.champions);
  }, [data]);

  // Kahraman (Hero) parça özeti — yeni kahraman özelliği olan kartlar için
  const heroPiecesSummary = useMemo(() => {
    if (!data?.heroes) return null;
    let unlockedCount = 0;
    let lockedCount = 0;
    let totalHeroLevels = 0;
    let ownedHeroLevels = 0;
    for (const h of data.heroes) {
      totalHeroLevels += h.max_hero_level || 1;
      if (h.unlocked) {
        unlockedCount += 1;
        ownedHeroLevels += h.hero_level || 0;
      } else {
        lockedCount += 1;
      }
    }
    return { unlockedCount, lockedCount, totalHeroLevels, ownedHeroLevels };
  }, [data]);

  const evoStats = useMemo(() => {
    if (!data?.evolutions) return null;
    let totalEvoLevels = 0;
    let unlockedEvoLevels = 0;
    for (const e of data.evolutions) {
      totalEvoLevels += e.max_evolution_level || 1;
      unlockedEvoLevels += e.evolution_level || 0;
    }
    // Her evrim seviyesi = 6 parça
    const SHARDS_PER_LEVEL = 6;
    const totalShards = totalEvoLevels * SHARDS_PER_LEVEL;
    const openShards = unlockedEvoLevels * SHARDS_PER_LEVEL;
    const missingShards = totalShards - openShards;
    return {
      totalEvoLevels,
      unlockedEvoLevels,
      totalShards,
      openShards,
      missingShards,
    };
  }, [data]);

  // ────────────────────────────────────────────────────────────────
  // Savaş Geçmişi: en çok kullanılan deste + o destenin win rate'i
  // ────────────────────────────────────────────────────────────────
  const battleStats = useMemo(() => {
    if (!battles || battles.length === 0) return null;
    // Her destenin fingerprint'i (kart id'leri sıralı) → { wins, losses, draws, sample }
    const map = new Map();
    for (const b of battles) {
      const decks = b.self?.decks?.length ? b.self.decks : (b.self?.cards ? [b.self.cards] : []);
      const won = (b.self?.crowns || 0) > (b.opponent?.crowns || 0);
      const draw = (b.self?.crowns || 0) === (b.opponent?.crowns || 0);
      for (const deck of decks) {
        if (!deck || deck.length < 8) continue;
        const key = [...deck].map((c) => c.id || c.name).sort().join("|");
        if (!map.has(key)) {
          map.set(key, { wins: 0, losses: 0, draws: 0, sample: deck, total: 0 });
        }
        const s = map.get(key);
        s.total += 1;
        if (draw) s.draws += 1;
        else if (won) s.wins += 1;
        else s.losses += 1;
      }
    }
    if (map.size === 0) return null;
    // En çok kullanılan (ties: en yüksek win rate)
    let best = null;
    for (const [, s] of map) {
      const decided = s.wins + s.losses;
      const wr = decided > 0 ? (s.wins / decided) * 100 : 0;
      const cur = { ...s, winRate: wr };
      if (
        !best ||
        cur.total > best.total ||
        (cur.total === best.total && cur.winRate > best.winRate)
      ) {
        best = cur;
      }
    }
    return best;
  }, [battles]);

  // Bir desteyi CR "kopyala" deep-link'ine dönüştür
  const buildDeckCopyUrl = useCallback((deck) => {
    if (!deck?.length) return "";
    const ids = deck.slice(0, 8).map((c) => c.id).filter(Boolean).join(";");
    return `https://link.clashroyale.com/en?clashroyale://copyDeck?deck=${ids}`;
  }, []);

  const copyDeck = useCallback(async (deck) => {
    const url = buildDeckCopyUrl(deck);
    if (!url) {
      toast.error("Deste bilgisi eksik");
      return;
    }
    let ok = false;
    try {
      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch { ok = false; }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        ok = document.execCommand && document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) toast.success("Deste linki kopyalandı");
    else toast.info("Kopyalanamadı — linki elle seçin");
  }, [buildDeckCopyUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Yükleniyor…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/")} className="border-white/10">
            <ArrowLeft className="h-4 w-4" /> <span className="ml-1">Geri</span>
          </Button>
        </header>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
          <p className="text-rose-300 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  const id = data.identity;
  const c = data.counts;
  const lg = leagueLabel(id);
  const inPoL =
    id.pol_league != null ||
    (id.pol_trophies || 0) > 0 ||
    (id.best_pol_trophies || 0) > 0 ||
    id.pol_rank ||
    id.best_pol_rank;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/")}
          className="border-white/10"
          data-testid="player-back"
        >
          <ArrowLeft className="h-4 w-4" /> <span className="ml-1">Oyuncular</span>
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <img
            src={`${process.env.PUBLIC_URL || ""}/site-icon.png`}
            alt="SalihiCr Stats"
            className="h-8 w-8 object-contain"
          />
          <span className="hidden sm:inline font-display text-sm tracking-wide">
            SalihiCr Stats
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3 sm:px-6 pb-16 space-y-4 sm:space-y-6">
        {/* Kimlik */}
        <section
          className="card-surface rounded-2xl border border-white/10 p-3 sm:p-6"
          data-testid="player-identity"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-xl sm:text-3xl tracking-wide truncate">
                  {id.name}
                </h1>
                <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-mono text-muted-foreground border border-white/5">
                  {id.tag}
                </span>
                {id.activity && (
                  <span
                    data-testid="player-activity-badge"
                    title={
                      id.activity.active
                        ? `Aktif · son ${Math.round(id.activity.minutes_since_active || 0)} dk`
                        : !id.activity.has_baseline
                        ? "Aktiflik verisi yeni topluyor — birazdan tekrar bak"
                        : id.activity.minutes_since_active != null
                        ? `Son aktif: ${Math.round(id.activity.minutes_since_active)} dk önce`
                        : "Henüz aktivite kaydı yok"
                    }
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${
                      id.activity.active
                        ? "bg-emerald-400/15 text-emerald-300 border-emerald-400/30"
                        : "bg-white/[0.04] text-muted-foreground border-white/10"
                    }`}
                  >
                    <span
                      className={`inline-block h-1.5 w-1.5 rounded-full ${
                        id.activity.active
                          ? "bg-emerald-400 animate-pulse"
                          : "bg-muted-foreground/50"
                      }`}
                    />
                    {id.activity.active
                      ? "Aktif"
                      : !id.activity.has_baseline
                      ? "Bekle"
                      : id.activity.minutes_since_active != null &&
                        id.activity.minutes_since_active < 60
                      ? `${Math.round(id.activity.minutes_since_active)}dk önce`
                      : id.activity.minutes_since_active != null &&
                        id.activity.minutes_since_active < 60 * 24
                      ? `${Math.round(id.activity.minutes_since_active / 60)}sa önce`
                      : "Çevrimdışı"}
                  </span>
                )}
              </div>
              {/* Klan bilgisi: rozet + isim + altına ARENA (küçük soluk) */}
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                {id.clan ? (
                  <>
                    <ClanBadge
                      badgeId={id.clan.badge_id}
                      size={28}
                      className="shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate flex items-center gap-1 flex-wrap">
                        {id.clan.name}
                        {id.role && (
                          <span className="text-xs text-muted-foreground font-normal">
                            · {ROLE_TR[id.role] || id.role}
                          </span>
                        )}
                      </div>
                      {id.arena && (
                        <div className="text-[10px] text-muted-foreground/70 leading-tight">
                          {id.arena}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center rounded-md bg-primary/15 text-primary w-7 h-7">
                      <Shield className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Klansız</div>
                      {id.arena && (
                        <div className="text-[10px] text-muted-foreground/70 leading-tight">
                          {id.arena}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a
                href={`https://royaleapi.com/player/${(id.tag || "").replace("#", "")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="player-royaleapi-link"
                title="RoyaleAPI'de aç"
                className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-md border border-white/10 bg-white/[0.03] text-xs font-semibold text-foreground hover:bg-white/[0.06] transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">RoyaleAPI</span>
              </a>
              <Button
                onClick={toggleSave}
                data-testid="player-save-toggle"
                size="sm"
                className={
                  saved
                    ? "bg-amber-400/15 text-amber-300 border border-amber-400/30 hover:bg-amber-400/25"
                    : "bg-primary text-primary-foreground hover:brightness-110"
                }
              >
                {saved ? (
                  <>
                    <StarOff className="h-4 w-4" /> <span className="ml-1.5">Kayıtlı</span>
                  </>
                ) : (
                  <>
                    <Star className="h-4 w-4" /> <span className="ml-1.5">Kaydet</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Stat grid — Kupa, En İyi, Kral Sv., Aşama/Lig */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat
              icon={Trophy}
              label="Kupa"
              value={fmt(id.trophies)}
              tone="text-amber-300"
            />
            <Stat icon={Trophy} label="En İyi Kupa" value={fmt(id.best_trophies)} />
            <Stat icon={Crown} label="Koleksiyon Sv." value={id.collection_level ?? id.exp_level ?? "-"} tone="text-primary" />
            <Stat
              icon={Sparkles}
              iconImg={
                id.pol_league != null && id.pol_league >= 1 && id.pol_league <= 7
                  ? `${process.env.PUBLIC_URL || ""}/icons/lig-${id.pol_league}.png`
                  : undefined
              }
              label={
                id.pol_league != null || (id.pol_trophies || 0) > 0
                  ? "Lig"
                  : "Aşama / Lig"
              }
              value={lg.main}
              tone={lg.tone}
              sub={lg.sub}
            />
          </div>

          {/* Rank/PoL kupa AYRI kutular — Efsaneler Yolu'ndaysa göster */}
          {inPoL ? (
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat
                icon={Trophy}
                label="Nihai Madalyası"
                value={fmt(id.pol_trophies || 0)}
                tone="text-amber-300"
              />
              <Stat
                icon={Award}
                label="Nihai Sıralaması"
                value={id.pol_rank ? `#${fmt(id.pol_rank)}` : "-"}
                tone="text-fuchsia-300"
              />
              <Stat
                icon={Trophy}
                iconImg={
                  id.best_pol_league != null && id.best_pol_league >= 1 && id.best_pol_league <= 7
                    ? `${process.env.PUBLIC_URL || ""}/icons/lig-${id.best_pol_league}.png`
                    : undefined
                }
                label="En İyi Madalya"
                value={
                  id.best_pol_trophies
                    ? `L${id.best_pol_league ?? "?"} · ${fmt(id.best_pol_trophies)}`
                    : "-"
                }
              />
              <Stat
                icon={Star}
                label="En İyi Sıralama"
                value={id.best_pol_rank ? `#${fmt(id.best_pol_rank)}` : "-"}
              />
            </div>
          ) : null}
        </section>

        {/* Tab bar */}
        <div
          className="flex gap-1 p-1 rounded-xl border border-white/10 bg-white/[0.02] overflow-x-auto"
          data-testid="player-tabs"
        >
          {[
            { key: "max", label: "Hesap Maxla", icon: Coins, img: `${process.env.PUBLIC_URL || ""}/icons/hesap-maxla.png` },
            { key: "cards", label: "Kartlar", icon: Swords, img: `${process.env.PUBLIC_URL || ""}/icons/kart.png` },
            { key: "evolutions", label: "Evrimler", icon: Sparkles, img: `${process.env.PUBLIC_URL || ""}/icons/evrim.png` },
            { key: "heroes", label: "Kahramanlar", icon: Crown, img: `${process.env.PUBLIC_URL || ""}/icons/kahraman.png` },
            { key: "battles", label: "Savaş Geçmişi", icon: History },
          ].map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setActiveTab(t.key)}
                className={`shrink-0 flex items-center justify-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-lg text-[11px] sm:text-xs font-bold uppercase tracking-wider transition-all ${
                  active
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
                }`}
              >
                {t.img ? (
                  <img src={t.img} alt="" className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 object-contain" />
                ) : (
                  <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                )}
                <span className="whitespace-nowrap">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* HESABI MAXLA paneli */}
        {activeTab === "max" && maxSummary && (
          <Section title="Hesabı Maxla" icon={Coins} id="max-account">
            <p className="text-xs text-muted-foreground mb-3">
              Tüm kartları 16. seviyeye çıkarmak için gereken kaynaklar. 16+ fazlalık
              kartlar <b>Kristal</b>&apos;e dönüşür.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-amber-300/80">
                  <img src={GOLD_ICON} alt="Altın" className="h-4 w-4 object-contain" /> Toplam Altın
                </div>
                <div className="mt-0.5 font-bold tabular-nums text-base sm:text-lg text-amber-300">
                  {fmt(maxSummary.totalGold)}
                </div>
              </div>
              <div className="rounded-xl border border-sky-400/20 bg-sky-400/[0.05] px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-sky-300/80">
                  <img src={CRYSTAL_ICON} alt="Kristal" className="h-3.5 w-3.5" />
                  Toplam Kristal
                </div>
                <div className="mt-0.5 font-bold tabular-nums text-base sm:text-lg text-sky-300">
                  {fmt(maxSummary.totalCrystals)}
                </div>
              </div>
              <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.05] px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-rose-300/80">
                  <Swords className="h-3.5 w-3.5" /> Eksik Kart
                </div>
                <div className="mt-0.5 font-bold tabular-nums text-base sm:text-lg text-rose-300">
                  {fmt(maxSummary.totalDeficitCards)}
                </div>
              </div>
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.05] px-3 py-2.5">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-300/80">
                  <Zap className="h-3.5 w-3.5" /> Fazla Kart (16+)
                </div>
                <div className="mt-0.5 font-bold tabular-nums text-base sm:text-lg text-emerald-300">
                  {fmt(maxSummary.totalSurplusCards)}
                </div>
              </div>
            </div>

            {/* Eksik evrim & kahraman özeti */}
            {data?.counts && (
              <div className="mt-2 grid grid-cols-2 gap-2" data-testid="max-missing-summary">
                <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-400/[0.05] px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-fuchsia-300/80">
                    <Sparkles className="h-3.5 w-3.5" /> Eksik Evrim
                  </div>
                  <div className="mt-0.5 font-bold tabular-nums text-base sm:text-lg text-fuchsia-300">
                    {fmt(Math.max(0, (data.counts.evolutions_total || 0) - (data.counts.evolutions_unlocked || 0)))}
                    <span className="text-xs text-muted-foreground font-normal"> / {fmt(data.counts.evolutions_total || 0)}</span>
                  </div>
                </div>
                <div className="rounded-xl border border-indigo-400/20 bg-indigo-400/[0.05] px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-indigo-300/80">
                    <Crown className="h-3.5 w-3.5" /> Eksik Kahraman
                  </div>
                  <div className="mt-0.5 font-bold tabular-nums text-base sm:text-lg text-indigo-300">
                    {fmt(Math.max(0, (data.counts.heroes_total || 0) - (data.counts.heroes_unlocked || 0)))}
                    <span className="text-xs text-muted-foreground font-normal"> / {fmt(data.counts.heroes_total || 0)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {["common", "rare", "epic", "legendary", "champion"].map((r) => {
                const b = maxSummary.byRarity[r];
                return (
                  <div
                    key={r}
                    className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5"
                  >
                    <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                      <span
                        className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${RARITY_COLOR[r]}`}
                      >
                        <img src={WILDCARD_IMG[r]} alt="" className="h-5 w-5 object-contain drop-shadow" />
                        {RARITY_TR[r]}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {b.maxedCount} MAX · {b.unmaxedCount} devam · {b.lockedCount} kilitli
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                      <div>
                        <div className="text-muted-foreground text-[9px] uppercase">Gereken Joker</div>
                        <div className="font-bold text-rose-300 tabular-nums">
                          {fmt(b.wildCardsNeeded)}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground text-[9px] uppercase flex items-center gap-1">
                          <img src={GOLD_ICON} alt="" className="h-3 w-3 object-contain" /> Altın
                        </div>
                        <div className="font-bold text-amber-300 tabular-nums">
                          {fmt(b.goldNeeded)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Kristal detay paneli — nadirlik bazında fazlalık kartlardan gelen kristal */}
            <div className="mt-3 rounded-xl border border-sky-400/20 bg-sky-400/[0.04] px-3 py-2.5">
              <div className="flex items-center gap-1.5 mb-2">
                <img src={CRYSTAL_ICON} alt="Kristal" className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-wider text-sky-300">
                  Kristal Detayı
                </span>
                <span className="ml-auto text-[10px] text-muted-foreground">
                  16+ fazla kart × oran = kristal
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 text-[11px]">
                {["common", "rare", "epic", "legendary", "champion"].map((r) => {
                  const b = maxSummary.byRarity[r];
                  return (
                    <div
                      key={`crystal-${r}`}
                      className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5"
                    >
                      <div className={`text-[9px] uppercase tracking-wider ${RARITY_COLOR[r]}`}>
                        {RARITY_TR[r]}
                      </div>
                      <div className="font-bold tabular-nums text-sky-300 text-sm">
                        {fmt(b.crystals)}
                      </div>
                      <div className="text-[8px] text-muted-foreground tabular-nums">
                        {fmt(b.surplusCards)} × {CRYSTAL_RATE[r]}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Section>
        )}

        {/* KOLEKSİYON SEVİYESİ HESAPLAMA MOTORU */}
        {activeTab === "max" && (() => {
          const lockedEvoCount = (data?.evolutions || []).filter((e) => !e.unlocked).length;
          const lockedHeroCount = (data?.champions || []).filter((c) => !c.unlocked).length;
          return (
          <Section title="Koleksiyon Seviyesi Hesaplama" icon={Award}>
            <div className="text-xs text-muted-foreground mb-3 space-y-0.5">
              <div>• 1 yeni kart seviyesi = <b>+1</b> koleksiyon seviyesi</div>
              <div>• 1 yeni evrim seviyesi = <b>+5</b> koleksiyon seviyesi</div>
              <div>• 1 yeni kahraman = <b>+5</b> koleksiyon seviyesi</div>
              {data?.counts && (
                <div className="pt-1 text-[11px] text-primary/70" data-testid="cl-max-breakdown">
                  Teorik max = {fmt(data.counts.cards_total || 0)}×16 (kart) + {fmt(data.counts.support_cards_total || 0)}×16 (kule) + {fmt(data.counts.evolutions_total || 0)}×5 (evrim) + {fmt(data.counts.heroes_total || 0)}×5 (kahraman) = <b>{fmt(computeMaxPossibleCL(data))}</b>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Mevcut Sv.</div>
                <div className="mt-0.5 font-bold tabular-nums text-lg">
                  {fmt(id.collection_level ?? id.exp_level ?? 0)}
                </div>
              </div>
              <div className="rounded-xl border border-primary/30 bg-primary/[0.05] px-3 py-2.5">
                <div className="flex items-center justify-between gap-1">
                  <div className="text-[10px] uppercase tracking-wider text-primary/80">Hedef Sv.</div>
                  <div className="text-[8px] text-muted-foreground tabular-nums" title="Ulaşılabilir maksimum">
                    max {fmt(computeMaxPossibleCL(data))}
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  data-testid="cl-target-input"
                  placeholder={String(id.collection_level ?? id.exp_level ?? 0)}
                  value={clTarget}
                  onChange={(e) => {
                    const cap = computeMaxPossibleCL(data);
                    let v = e.target.value.replace(/[^0-9]/g, "");
                    if (v !== "" && parseInt(v, 10) > cap) v = String(cap);
                    setClTarget(v);
                    setClResult(null);
                  }}
                  className="mt-0.5 w-full bg-transparent font-bold tabular-nums text-lg text-primary placeholder:text-primary/40 focus:outline-none"
                />
              </div>
              <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-400/[0.05] px-3 py-2.5">
                <div className="flex items-center justify-between gap-1">
                  <div className="text-[10px] uppercase tracking-wider text-fuchsia-300/80">Yeni Evrim</div>
                  <div className="text-[8px] text-muted-foreground tabular-nums" title="Kilitli evrim sayısı">
                    max {lockedEvoCount}
                  </div>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  data-testid="cl-newevos-input"
                  placeholder="0"
                  value={clNewEvos}
                  onChange={(e) => {
                    let v = e.target.value.replace(/[^0-9]/g, "");
                    if (v !== "" && parseInt(v, 10) > lockedEvoCount) v = String(lockedEvoCount);
                    setClNewEvos(v);
                    setClResult(null);
                  }}
                  className="mt-0.5 w-full bg-transparent font-bold tabular-nums text-lg text-fuchsia-300 placeholder:text-fuchsia-300/40 focus:outline-none"
                />
                <div className="text-[9px] text-muted-foreground">= +{(parseInt(clNewEvos || "0", 10)) * 5} sv</div>
              </div>
              <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] px-3 py-2.5">
                <div className="flex items-center justify-between gap-1">
                  <div className="text-[10px] uppercase tracking-wider text-amber-300/80">Yeni Kahraman</div>
                  <div className="text-[8px] text-muted-foreground tabular-nums" title="Kilitli kahraman sayısı">
                    max {lockedHeroCount}
                  </div>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  data-testid="cl-newheroes-input"
                  placeholder="0"
                  value={clNewHeroes}
                  onChange={(e) => {
                    let v = e.target.value.replace(/[^0-9]/g, "");
                    if (v !== "" && parseInt(v, 10) > lockedHeroCount) v = String(lockedHeroCount);
                    setClNewHeroes(v);
                    setClResult(null);
                  }}
                  className="mt-0.5 w-full bg-transparent font-bold tabular-nums text-lg text-amber-300 placeholder:text-amber-300/40 focus:outline-none"
                />
                <div className="text-[9px] text-muted-foreground">= +{(parseInt(clNewHeroes || "0", 10)) * 5} sv</div>
              </div>
            </div>

            {(() => {
              const cur = id.collection_level ?? id.exp_level ?? 0;
              const tgt = parseInt(clTarget || "0", 10);
              const evos = parseInt(clNewEvos || "0", 10);
              const heroes = parseInt(clNewHeroes || "0", 10);
              const delta = Math.max(0, tgt - cur);
              const fromExtras = evos * 5 + heroes * 5;
              const needCards = Math.max(0, delta - fromExtras);
              if (!tgt || tgt <= cur) {
                return (
                  <div className="text-xs text-muted-foreground py-2">
                    Hedef seviyeyi mevcut seviyeden büyük gir.
                  </div>
                );
              }
              return (
                <>
                  <div className="text-[11px] text-muted-foreground mb-2">
                    Toplam fark: <b className="text-foreground">{delta}</b> sv ·
                    Evrim/Kahramandan gelecek: <b className="text-foreground">{fromExtras}</b> ·
                    Karttan gerekli: <b className="text-foreground">{needCards}</b>
                  </div>

                  {/* Ana butonlar */}
                  <div className="flex gap-2 flex-wrap mb-3">
                    <button
                      data-testid="cl-calc-nojoker"
                      onClick={() => {
                        const res = computeCollectionLevelPlan({
                          currentLevel: cur,
                          targetLevel: tgt,
                          newEvos: evos,
                          newHeroes: heroes,
                          cards: clCards,
                          useJoker: false,
                        });
                        setClResult(res);
                        setClMode("nojoker");
                        setClJokerVariant(null);
                      }}
                      className={`flex-1 min-w-[140px] rounded-lg text-white text-xs font-bold px-3 py-2 ${
                        clMode === "nojoker"
                          ? "bg-emerald-500 ring-2 ring-emerald-300/60"
                          : "bg-emerald-500/90 hover:bg-emerald-500"
                      }`}
                    >
                      Joker Kullanmadan
                    </button>
                    <button
                      data-testid="cl-calc-joker"
                      onClick={() => {
                        // Sub-menu aç (henüz hesap yapma)
                        setClJokerVariant((prev) => (prev ? null : "auto"));
                        setClMode("joker");
                        if (!clJokerVariant) setClResult(null);
                      }}
                      className={`flex-1 min-w-[140px] rounded-lg text-white text-xs font-bold px-3 py-2 ${
                        clMode === "joker"
                          ? "bg-rose-500 ring-2 ring-rose-300/60"
                          : "bg-rose-500/90 hover:bg-rose-500"
                      }`}
                    >
                      Joker Kullanarak
                    </button>
                  </div>

                  {/* Joker alt-menüsü: Otomatik / Manuel Gir */}
                  {clMode === "joker" && clJokerVariant && (
                    <div className="rounded-xl border border-rose-400/25 bg-rose-500/[0.04] px-3 py-2.5 mb-3">
                      <div className="flex gap-2 flex-wrap mb-2">
                        <button
                          data-testid="cl-joker-auto"
                          onClick={() => {
                            setClJokerVariant("auto");
                            const res = computeCollectionLevelPlan({
                              currentLevel: cur,
                              targetLevel: tgt,
                              newEvos: evos,
                              newHeroes: heroes,
                              cards: clCards,
                              useJoker: true,
                            });
                            setClResult(res);
                          }}
                          className={`flex-1 min-w-[120px] rounded-lg text-xs font-bold px-3 py-1.5 ${
                            clJokerVariant === "auto"
                              ? "bg-rose-500 text-white ring-2 ring-rose-300/60"
                              : "bg-rose-500/60 text-white hover:bg-rose-500/80"
                          }`}
                        >
                          Otomatik
                        </button>
                        <button
                          data-testid="cl-joker-manual"
                          onClick={() => {
                            setClJokerVariant("manual");
                            setClResult(null);
                          }}
                          className={`flex-1 min-w-[120px] rounded-lg text-xs font-bold px-3 py-1.5 ${
                            clJokerVariant === "manual"
                              ? "bg-fuchsia-500 text-white ring-2 ring-fuchsia-300/60"
                              : "bg-fuchsia-500/60 text-white hover:bg-fuchsia-500/80"
                          }`}
                        >
                          Jokerleri Manuel Gir
                        </button>
                      </div>

                      {clJokerVariant === "manual" && (
                        <div className="space-y-2">
                          <div className="text-[10px] text-muted-foreground">
                            Kullanılabilecek joker sayılarını gir; hesaplama bu limitleri aşmaz.
                          </div>
                          <div className="grid grid-cols-5 gap-1.5">
                            {["common", "rare", "epic", "legendary", "champion"].map((r) => (
                              <div
                                key={r}
                                className={`rounded-lg border ${
                                  {
                                    common: "border-slate-300/25 bg-slate-400/[0.05]",
                                    rare: "border-orange-400/30 bg-orange-500/[0.05]",
                                    epic: "border-fuchsia-400/30 bg-fuchsia-500/[0.05]",
                                    legendary: "border-rose-400/30 bg-rose-500/[0.05]",
                                    champion: "border-amber-400/40 bg-amber-500/[0.05]",
                                  }[r]
                                } px-1.5 py-1.5 flex flex-col items-center gap-1`}
                              >
                                <img
                                  src={WILDCARD_IMG[r]}
                                  alt={WILDCARD_TR[r]}
                                  className="h-7 w-7 object-contain"
                                />
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  data-testid={`cl-joker-manual-${r}`}
                                  placeholder="0"
                                  value={clManualJokers[r]}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/[^0-9]/g, "");
                                    setClManualJokers((prev) => ({ ...prev, [r]: v }));
                                  }}
                                  className={`w-full bg-transparent text-center font-bold tabular-nums text-sm ${RARITY_COLOR[r]} placeholder:opacity-40 focus:outline-none`}
                                />
                              </div>
                            ))}
                          </div>
                          <button
                            data-testid="cl-joker-manual-calc"
                            onClick={() => {
                              const caps = {
                                common: parseInt(clManualJokers.common || "0", 10),
                                rare: parseInt(clManualJokers.rare || "0", 10),
                                epic: parseInt(clManualJokers.epic || "0", 10),
                                legendary: parseInt(clManualJokers.legendary || "0", 10),
                                champion: parseInt(clManualJokers.champion || "0", 10),
                              };
                              const res = computeCollectionLevelPlan({
                                currentLevel: cur,
                                targetLevel: tgt,
                                newEvos: evos,
                                newHeroes: heroes,
                                cards: clCards,
                                useJoker: true,
                                jokerCaps: caps,
                              });
                              setClResult(res);
                            }}
                            className="w-full rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-xs font-bold px-3 py-2"
                          >
                            Hesapla
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {clResult && (
              <div className="space-y-2">
                {!clResult.feasible && (
                  <div className="rounded-lg border border-rose-400/30 bg-rose-400/[0.06] px-3 py-2 text-[11px] text-rose-300">
                    Hedefe ulaşmak mümkün değil — {clResult.remainingNeeded} kart yükseltmesi
                    daha gerekiyor. {clMode === "nojoker"
                      ? "Joker kullanarak yeniden hesapla."
                      : "Daha yüksek seviye kartlar veya daha çok evrim/kahraman gerekiyor."}
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <div className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-amber-300/80 flex items-center gap-1">
                      <img src={GOLD_ICON} alt="" className="h-3.5 w-3.5 object-contain" /> Toplam Altın
                    </div>
                    <div className="font-bold tabular-nums text-amber-300 flex items-center gap-1.5">
                      <img src={GOLD_ICON} alt="" className="h-4 w-4 object-contain" />
                      {fmt(clResult.totalGold)}
                    </div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Yükseltme Sayısı</div>
                    <div className="font-bold tabular-nums">{clResult.upgradesChosen}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Mod</div>
                    <div className="font-bold text-sm">
                      {clMode === "joker" ? "Joker + Altın" : "Sadece Altın"}
                    </div>
                  </div>
                </div>

                {clMode === "joker" && (
                  <div className="rounded-xl border border-rose-400/20 bg-rose-400/[0.04] px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wider text-rose-300/80 mb-1.5">
                      Gereken Joker (nadirlik bazında)
                    </div>
                    <div className="grid grid-cols-5 gap-1.5 text-[11px]">
                      {["common", "rare", "epic", "legendary", "champion"].map((r) => (
                        <div key={r} className="text-center flex flex-col items-center gap-1">
                          <img
                            src={WILDCARD_IMG[r]}
                            alt={WILDCARD_TR[r]}
                            className="h-8 w-8 sm:h-9 sm:w-9 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
                          />
                          <div className={`text-[9px] uppercase leading-none ${RARITY_COLOR[r]}`}>{RARITY_TR[r]}</div>
                          <div className="font-bold tabular-nums text-sm">
                            {fmt(clResult.wildCardsByRarity[r] || 0)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Kazanılacak ödüller (mevcut sv → hedef sv arasında) */}
                <CollectionRewardsPanel
                  current={id.collection_level ?? id.exp_level ?? 0}
                  target={parseInt(clTarget || "0", 10)}
                />

                {/* Yükseltilecek kart listesi (ardışık seviyeler birleştirilmiş) */}
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Yükseltilecek Kartlar
                  </div>
                  <ul
                    className="space-y-1 max-h-64 overflow-y-auto pr-1"
                    data-testid="cl-upgrade-list"
                  >
                    {mergeConsecutiveUpgrades(clResult.upgrades).map((u, i) => (
                      <li
                        key={`${u.cardId}-${u.toLevel}-${i}`}
                        className="flex items-center justify-between gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5 text-[11px]"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className={`inline-block h-1.5 w-1.5 rounded-full ${
                              u.rarity === "common"
                                ? "bg-slate-300"
                                : u.rarity === "rare"
                                ? "bg-orange-400"
                                : u.rarity === "epic"
                                ? "bg-fuchsia-400"
                                : u.rarity === "legendary"
                                ? "bg-rose-400"
                                : "bg-amber-400"
                            }`}
                          />
                          <span className="font-semibold truncate">{u.name}</span>
                          <span className="text-muted-foreground shrink-0">
                            Sv {u.fromLevel} → {u.toLevel}
                          </span>
                        </div>
                        <span className="text-amber-300 font-bold tabular-nums shrink-0 inline-flex items-center gap-1">
                          <img src={GOLD_ICON} alt="" className="h-3.5 w-3.5 object-contain" />
                          {fmt(u.gold)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </Section>
          );
        })()}

        {/* Kartlar */}
        {activeTab === "cards" && (
          <Section
            title="Kartlar"
            icon={Swords}
            subtitle={
              c.support_cards > 0
                ? `${c.cards}/${c.cards_total} kart · ${c.support_cards}/${c.support_cards_total} kule`
                : `${c.cards}/${c.cards_total} açık`
            }
          >
            {/* Sıralama kontrolleri */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="inline-flex rounded-lg border border-white/10 bg-white/[0.03] p-0.5 text-[11px]">
                <button
                  onClick={() => setCardSortMode("rarity")}
                  data-testid="sort-rarity"
                  className={`px-2.5 py-1 rounded-md font-semibold ${
                    cardSortMode === "rarity"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Nadirlik
                </button>
                <button
                  onClick={() => setCardSortMode("maxness")}
                  data-testid="sort-maxness"
                  className={`px-2.5 py-1 rounded-md font-semibold ${
                    cardSortMode === "maxness"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Max&apos;a Yakınlık
                </button>
              </div>
              <button
                onClick={() =>
                  setCardSortDir(cardSortDir === "desc" ? "asc" : "desc")
                }
                data-testid="sort-direction"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-white/10 bg-white/[0.03] text-[11px] font-semibold text-foreground hover:bg-white/[0.06]"
                title={cardSortDir === "desc" ? "Azalan" : "Artan"}
              >
                {cardSortDir === "desc" ? (
                  <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                ) : (
                  <ArrowUpNarrowWide className="h-3.5 w-3.5" />
                )}
                {cardSortDir === "desc" ? "Azalan" : "Artan"}
              </button>
            </div>

            <div
              className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1"
              data-testid="cards-grid"
            >
              {cardsSorted.map((card) => (
                <CardTile key={`c-${card.id}`} item={card} />
              ))}
              {lockedCardsSorted.map((card) => (
                <CardTile key={`lc-${card.id}`} item={card} locked />
              ))}
            </div>

            {/* Kule Askerleri (Tower Troops) */}
            {data.support_cards?.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-4 w-1 rounded-full bg-cyan-400/70" />
                  <div className="text-[11px] font-bold uppercase tracking-wider text-cyan-300">
                    Kule Askerleri
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {data.support_cards.length} adet
                  </div>
                </div>
                <div
                  className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1"
                  data-testid="support-cards-grid"
                >
                  {data.support_cards.map((card) => (
                    <CardTile key={`sc-${card.id}`} item={card} hideCount rawLevel />
                  ))}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Evrimler */}
        {activeTab === "evolutions" && (
          <Section
            title="Evrimler"
            icon={Sparkles}
            subtitle={`${c.evolutions_unlocked}/${c.evolutions_total} açık`}
          >
            <div
              className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1"
              data-testid="evolutions-grid"
            >
              {evolutionsSorted.map((e) => (
                <CardTile key={`e-${e.id}`} item={e} evo locked={!e.unlocked} />
              ))}
            </div>
          </Section>
        )}

        {/* Kahramanlar (Hero özelliği olan kartlar) */}
        {activeTab === "heroes" && (
          <Section
            title="Kahramanlar"
            icon={Crown}
            subtitle={`${c.heroes_unlocked}/${c.heroes_total} açık`}
          >
            <div
              className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-1"
              data-testid="heroes-grid"
            >
              {heroesSorted.map((h) => (
                <CardTile
                  key={`h-${h.id}`}
                  item={h}
                  locked={!h.unlocked}
                  hideCount
                />
              ))}
            </div>
          </Section>
        )}

        {/* Savaş Geçmişi */}
        {activeTab === "battles" && (
          <Section title="Savaş Geçmişi" icon={History}>
            {battlesLoading ? (
              <div className="py-8 flex items-center justify-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Savaşlar yükleniyor…
              </div>
            ) : !battles || battles.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Son savaş kaydı bulunamadı.
              </div>
            ) : (
              <div className="space-y-3">
                {battleStats && (
                  <div
                    data-testid="most-used-deck"
                    className="rounded-xl border border-amber-400/25 bg-gradient-to-br from-amber-500/10 to-transparent p-3"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="rounded-md p-1.5 bg-amber-500/15 text-amber-300 shrink-0">
                          <Flame className="h-4 w-4" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-display text-sm tracking-wide leading-tight text-amber-200">
                            En Çok Kullanılan Deste
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            Son {battles.length} savaşta bu deste{" "}
                            <b className="text-foreground">{battleStats.total}</b> kez kullanıldı ·{" "}
                            <span className="text-emerald-300 font-semibold">
                              {battleStats.wins}G
                            </span>
                            {battleStats.draws > 0 && (
                              <>
                                {" "}
                                ·{" "}
                                <span className="text-muted-foreground font-semibold">
                                  {battleStats.draws}B
                                </span>
                              </>
                            )}{" "}
                            ·{" "}
                            <span className="text-rose-300 font-semibold">
                              {battleStats.losses}Y
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div
                          className="rounded-lg border border-emerald-400/25 bg-emerald-400/[0.06] px-2.5 py-1 text-center"
                          data-testid="most-used-winrate"
                        >
                          <div className="text-[9px] uppercase tracking-wider text-emerald-300/80">
                            Win Rate
                          </div>
                          <div className="text-base font-bold tabular-nums text-emerald-300 leading-tight">
                            %{battleStats.winRate.toFixed(1)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => copyDeck(battleStats.sample)}
                          data-testid="most-used-copy"
                          title="Bu desteyi CR'a kopyala"
                          className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider hover:brightness-110"
                        >
                          <Copy className="h-3.5 w-3.5" />
                          Kopyala
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {battleStats.sample.map((c, j) => (
                        <img
                          key={`bstat-${j}`}
                          src={c.icon}
                          onError={(e) => {
                            if (c.icon_alt && e.target.src !== c.icon_alt)
                              e.target.src = c.icon_alt;
                          }}
                          alt={c.name}
                          className="h-9 w-9 object-contain"
                          title={`${c.name} · Sv ${c.level}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <ul className="space-y-2" data-testid="battles-list">
                  {battles.map((b, i) => {
                    const won = (b.self.crowns || 0) > (b.opponent.crowns || 0);
                    const draw = (b.self.crowns || 0) === (b.opponent.crowns || 0);
                    const selfDecks = b.self?.decks?.length ? b.self.decks : (b.self?.cards ? [b.self.cards] : []);
                    const oppDecks = b.opponent?.decks?.length ? b.opponent.decks : (b.opponent?.cards ? [b.opponent.cards] : []);
                    const isDuel = b.is_duel || selfDecks.length > 1;
                    return (
                      <li
                        key={i}
                        className="rounded-xl border border-white/10 bg-white/[0.02] p-2.5"
                      >
                        <div className="flex items-center justify-between text-[11px] mb-1.5 flex-wrap gap-1">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span
                              className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                                won
                                  ? "bg-emerald-400/15 text-emerald-300"
                                  : draw
                                  ? "bg-white/10 text-muted-foreground"
                                  : "bg-rose-400/15 text-rose-300"
                              }`}
                            >
                              {won ? "Galibiyet" : draw ? "Berabere" : "Yenilgi"}
                            </span>
                            <span className="text-muted-foreground truncate">
                              {b.mode || b.type}
                            </span>
                            {isDuel && (
                              <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase bg-fuchsia-400/15 text-fuchsia-300">
                                Düello · {selfDecks.length} deste
                              </span>
                            )}
                            {b.self.starting_trophies != null && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded-md bg-amber-400/10 text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums"
                                title="Bu savaşın oynandığı kupa"
                              >
                                <Trophy className="h-2.5 w-2.5" />
                                {fmt(b.self.starting_trophies)}
                              </span>
                            )}
                          </div>
                          <span className="text-muted-foreground tabular-nums text-[10px]">
                            {b.time
                              ? new Date(
                                  b.time.replace(
                                    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(\.\d+)?Z$/,
                                    "$1-$2-$3T$4:$5:$6Z",
                                  ),
                                ).toLocaleString("tr-TR", {
                                  dateStyle: "short",
                                  timeStyle: "short",
                                })
                              : ""}
                          </span>
                        </div>

                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <div className="min-w-0 flex-1">
                            <div className="text-[11px] font-semibold truncate">
                              {b.self.name}
                              {b.self.clan && (
                                <span className="text-muted-foreground font-normal ml-1">
                                  · {b.self.clan}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-center shrink-0 px-2">
                            {isDuel ? (
                              <div
                                className="font-bold tabular-nums text-sm"
                                data-testid={`duel-round-score-${i}`}
                              >
                                <span
                                  className={
                                    b.self_rounds_won > b.opp_rounds_won
                                      ? "text-emerald-300"
                                      : "text-muted-foreground"
                                  }
                                >
                                  {b.self_rounds_won}
                                </span>
                                <span className="text-muted-foreground">-</span>
                                <span
                                  className={
                                    b.opp_rounds_won > b.self_rounds_won
                                      ? "text-rose-300"
                                      : "text-muted-foreground"
                                  }
                                >
                                  {b.opp_rounds_won}
                                </span>
                              </div>
                            ) : (
                              <div className="font-bold tabular-nums text-sm">
                                {b.self.crowns} - {b.opponent.crowns}
                              </div>
                            )}
                            <div className="text-[9px] text-muted-foreground">{b.arena}</div>
                            {b.self.trophy_change != null && (
                              <div
                                className={`text-[10px] tabular-nums font-semibold ${
                                  b.self.trophy_change > 0
                                    ? "text-emerald-300"
                                    : b.self.trophy_change < 0
                                    ? "text-rose-300"
                                    : "text-muted-foreground"
                                }`}
                              >
                                {b.self.trophy_change > 0 ? "+" : ""}
                                {b.self.trophy_change}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1 text-right">
                            <div className="text-[11px] font-semibold truncate">
                              {b.opponent.tag ? (
                                <button
                                  type="button"
                                  data-testid="battle-opponent-link"
                                  onClick={() =>
                                    navigate(
                                      `/player/${(b.opponent.tag || "").replace("#", "")}`,
                                    )
                                  }
                                  className="text-sky-300 hover:text-sky-200 hover:underline underline-offset-2 truncate max-w-full"
                                  title="Rakibin profilini aç"
                                >
                                  {b.opponent.name || b.opponent.tag}
                                </button>
                              ) : (
                                <>{b.opponent.name || "-"}</>
                              )}
                              {b.opponent.clan && (
                                <span className="text-muted-foreground font-normal ml-1">
                                  · {b.opponent.clan}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Düello Skor Bar (görsel) */}
                        {isDuel && (b.self_rounds_won + b.opp_rounds_won) > 0 && (
                          <div className="mb-1.5 flex h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                            <div
                              className="bg-emerald-400"
                              style={{
                                width: `${
                                  ((b.self_rounds_won || 0) /
                                    ((b.self_rounds_won || 0) + (b.opp_rounds_won || 0))) *
                                  100
                                }%`,
                              }}
                            />
                            <div
                              className="bg-rose-400"
                              style={{
                                width: `${
                                  ((b.opp_rounds_won || 0) /
                                    ((b.self_rounds_won || 0) + (b.opp_rounds_won || 0))) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        )}

                        {/* Destelerin karşılıklı yerleşimi: SOL = bizim, SAĞ = rakip */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                          {/* Bizim */}
                          <div className="space-y-1">
                            <div className="text-[9px] uppercase tracking-wider text-emerald-300/80 font-bold flex items-center gap-1">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Bizim
                            </div>
                            {selfDecks.map((deck, di) => {
                              const dcrown = b.self.deck_crowns?.[di];
                              return (
                                <div
                                  key={`sd-${di}`}
                                  className="flex items-center gap-1.5 rounded-lg border border-emerald-400/10 bg-emerald-400/[0.03] p-1"
                                  data-testid={`battle-deck-${i}-${di}`}
                                >
                                  {isDuel && (
                                    <span className="text-[9px] font-bold text-emerald-300 shrink-0 w-4 text-center">
                                      #{di + 1}
                                    </span>
                                  )}
                                  <div className="flex-1 min-w-0 flex flex-wrap gap-0.5">
                                    {deck.slice(0, 8).map((c, j) => (
                                      <img
                                        key={j}
                                        src={c.icon}
                                        onError={(e) => {
                                          if (c.icon_alt && e.target.src !== c.icon_alt)
                                            e.target.src = c.icon_alt;
                                        }}
                                        alt={c.name}
                                        className="h-7 w-7 object-contain"
                                        title={`${c.name} · Sv ${c.level}`}
                                      />
                                    ))}
                                  </div>
                                  {isDuel && dcrown != null && (
                                    <span
                                      className="text-[10px] font-bold tabular-nums text-amber-300 inline-flex items-center gap-0.5 shrink-0"
                                      title={`Bu destede kazanılan taç: ${dcrown}`}
                                    >
                                      <Crown className="h-2.5 w-2.5" />
                                      {dcrown}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => copyDeck(deck)}
                                    title="Bu desteyi CR'a kopyala"
                                    data-testid={`deck-copy-${i}-${di}`}
                                    className="shrink-0 inline-flex items-center gap-0.5 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                                  >
                                    <Copy className="h-2.5 w-2.5" />
                                    Kopya
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          {/* Rakip */}
                          <div className="space-y-1">
                            <div className="text-[9px] uppercase tracking-wider text-rose-300/80 font-bold flex items-center gap-1 justify-end md:justify-end">
                              Rakip <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
                            </div>
                            {oppDecks.map((deck, di) => {
                              const dcrown = b.opponent.deck_crowns?.[di];
                              return (
                                <div
                                  key={`od-${di}`}
                                  className="flex items-center gap-1.5 rounded-lg border border-rose-400/10 bg-rose-400/[0.03] p-1"
                                  data-testid={`battle-opp-deck-${i}-${di}`}
                                >
                                  {isDuel && (
                                    <span className="text-[9px] font-bold text-rose-300 shrink-0 w-4 text-center">
                                      #{di + 1}
                                    </span>
                                  )}
                                  <div className="flex-1 min-w-0 flex flex-wrap gap-0.5">
                                    {deck.slice(0, 8).map((c, j) => (
                                      <img
                                        key={j}
                                        src={c.icon}
                                        onError={(e) => {
                                          if (c.icon_alt && e.target.src !== c.icon_alt)
                                            e.target.src = c.icon_alt;
                                        }}
                                        alt={c.name}
                                        className="h-7 w-7 object-contain"
                                        title={`${c.name} · Sv ${c.level}`}
                                      />
                                    ))}
                                  </div>
                                  {isDuel && dcrown != null && (
                                    <span
                                      className="text-[10px] font-bold tabular-nums text-amber-300 inline-flex items-center gap-0.5 shrink-0"
                                      title={`Rakibin bu destede kazandığı taç: ${dcrown}`}
                                    >
                                      <Crown className="h-2.5 w-2.5" />
                                      {dcrown}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => copyDeck(deck)}
                                    title="Rakibin destesini CR'a kopyala"
                                    data-testid={`opp-deck-copy-${i}-${di}`}
                                    className="shrink-0 inline-flex items-center gap-0.5 rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground hover:text-primary hover:border-primary/40 transition-colors"
                                  >
                                    <Copy className="h-2.5 w-2.5" />
                                    Kopya
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </Section>
        )}
      </main>
    </div>
  );
}
